<h2 class="subtitle" grtSubtitle>Matrix</h2>
<div class="sentence-matrix">
    <table class="table is-striped is-fullwidth" *ngIf="indexedTokens">
        <thead>
            <tr>
                <th [ngClass]="{ 'is-disabled': isCustomXPath }">Sentence</th>
                <td *ngFor="let token of indexedTokens" [ngClass]="{
                    'is-clickable': !isCustomXPath,
                    'is-disabled': isCustomXPath
                }" (click)="setTokenPart(token, 'na')">{{token.value}}</td>
            </tr>
        </thead>
        <tbody>
            <ng-container *ngFor="let option of options">
                <tr *ngIf="!option.advanced || showAdvanced">
                    <th [@fade] [ngClass]="{
                        'is-clickable': !isCustomXPath,
                        'is-disabled': isCustomXPath
                    }" (click)="setTokenRow(option.key)">
                        <span tooltipPosition="right" pTooltip="{{isCustomXPath ? undefined : option.description}}">
                            {{option.label}}
                            <fa-icon *ngIf="option.description" [icon]="faCircleInfo" aria-hidden="true"></fa-icon>
                        </span>
                    </th>
                    <td [@fade] *ngFor="let token of indexedTokens" (click)="setTokenPart(token, option.key)">
                        <grt-matrix-option [value]="attributes[token.index][option.key]" [disabled]="isCustomXPath"
                            [dependent]="tokenDependents[token.index][option.key]" [option]="option"></grt-matrix-option>
                    </td>
                </tr>
            </ng-container>
        </tbody>
    </table>
</div>
<div class="columns">
    <div class="column">
        <div class="content">
            <p *ngIf="!explanation">
                In the matrix above, you can indicate the relevant parts of the construction, i.e. the lexical items
                that should be included in the query, as well as their level of abstraction. Your selection is
                visualized by means of the <em>query tree</em> at the bottom of this page.
                The syntactic information provided by the parse in the previous step is automatically included.
            </p>
            <div class="notification is-dark" *ngIf="explanation">
                {{explanation}}
            </div>
        </div>
    </div>
    <div class="column">
        <div class="field">
            <label class="checkbox" [attr.disabled]="alwaysAdvanced || isCustomXPath || null"
                (mouseout)="explanation = undefined"
                (mouseover)="explanation = 'The advanced options offer more possibilities to modify your search instruction.'">
                <input type="checkbox" name="advanced" [(ngModel)]="showAdvanced"
                    [disabled]="alwaysAdvanced || isCustomXPath">
                Enable advanced options
                <fa-icon [icon]="faCircleInfo" aria-hidden="true"></fa-icon>
            </label>
        </div>
        <div class="field" (mouseout)="explanation = undefined"
            (mouseover)="explanation = 'Only search for patterns that have the same word order as your input example.'">
            <label class="checkbox" [attr.disabled]="isCustomXPath || null">
                <input type="checkbox" name="respectOrder" [checked]="respectOrder"
                    (click)="toggleSetting('respectOrder')" [disabled]="isCustomXPath"> Respect
                word order
                <fa-icon [icon]="faCircleInfo" aria-hidden="true"></fa-icon>
            </label>
        </div>
        <div class="field" (mouseout)="explanation = undefined"
            (mouseover)="explanation = 'In the results, show the sentence before and after the matching sentence to provide a broader context.'">
            <label class="checkbox" [attr.disabled]="isCustomXPath || null">
                <input type="checkbox" name="retrieveContext" [checked]="retrieveContext"
                    (click)="toggleSetting('retrieveContext')" [disabled]="isCustomXPath">
                Include context in results
                <label>
                    <fa-icon [icon]="faCircleInfo"></fa-icon>
                </label>
            </label>
        </div>
        <div class="field" (mouseout)="explanation = undefined"
            (mouseover)="explanation = 'Search for more general patterns by ignoring the properties of the top node, e.g. search for both main clauses and subclauses.'">
            <label class="checkbox" [attr.disabled]="isCustomXPath || null">
                <input type="checkbox" name="ignoreTopNode" [checked]="ignoreTopNode"
                    (click)="toggleSetting('ignoreTopNode')" [disabled]="isCustomXPath"> Ignore
                properties of the dominating node
                <fa-icon [icon]="faCircleInfo" aria-hidden="true"></fa-icon>
            </label>
        </div>
    </div>
</div>

<div class="columns">
    <div class="column xpath">
        <h2 class="subtitle">
            <div class="level">
                <div class="level-item level-left">
                    XPath Search Instruction
                </div>
                <div class="level-item level-right" *ngIf="loading">
                    <div class="field">
                        <p class="control">
                            <a class="button is-loading is-warning" role="button">
                                <span class="icon is-large">
                                </span>
                            </a>
                        </p>
                    </div>
                </div>
                <div class="level-item level-right" *ngIf="!loading">
                    <div class="field">
                        <p class="control" *ngIf="!isCustomXPath">
                            <a class="button" role="button" tooltipPosition="bottom"
                                pTooltip="Edit query. This will disable the matrix." (click)="editXPath()">
                                <span class="icon is-large">
                                    <fa-icon [icon]="faEdit" aria-hidden="true"></fa-icon>
                                </span>
                            </a>
                        </p>
                        <p class="control" *ngIf="isCustomXPath">
                            <a class="button is-danger" tooltipPosition="bottom"
                                pTooltip="Reset query. The query will be generated based on the options selected in the matrix."
                                (click)="resetXPath()">
                                <span class="icon is-large">
                                    <fa-icon [icon]="faTimes" aria-hidden="true"></fa-icon>
                                </span>
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </h2>
        <grt-xpath-viewer *ngIf="!isCustomXPath" (click)="editXPath()" [value]="xpath"></grt-xpath-viewer>
        <grt-xpath-editor *ngIf="isCustomXPath" width="100%" [value]="xpath" (onChange)="changeCustomXpath($event)"
            autofocus="true"></grt-xpath-editor>
        <p *ngIf="!isCustomXPath">
            <br />
            This is the search instruction which has been created based on the parse and your selection.
        </p>
    </div>
    <div class="column">
        <h2 class="subtitle">
            <div class="level">
                <div class="level-item level-left">
                    Tree
                </div>
                <div class="level-item level-right is-hidden-print">
                    <p class="control">
                        <a class="button" role="button" (click)="subTreeDisplay = 'both'" tooltipPosition="bottom"
                            pTooltip="Full screen display of the tree.">
                            <span class="icon is-large">
                                <fa-icon [icon]="faExpand" aria-hidden="true"></fa-icon>
                            </span>
                        </a>
                    </p>
                </div>
            </div>
        </h2>
        <p *ngIf="!subTreeXml;else tree">Loading ...</p>
        <ng-template #tree>
            <grt-tree-visualizer [filename]="filename" [xml]="subTreeXml" [display]="subTreeDisplay"
                [fullScreenButton]="false" [showMatrixDetails]="true" (displayChange)="subTreeDisplay = $event">
            </grt-tree-visualizer>
        </ng-template>
    </div>
</div>
